/**
 * What the portal tells a practice about its referral commission on one
 * client: the badge in the clients table and the steps in the "Referral
 * progress" sheet.
 *
 * Whether a commission is earned is decided by the backend
 * (`_get_commission_status` in backend/api/views/accountant_users.py), which
 * follows the payout rules of `manage.py commission_report`. Nothing here
 * works that out again from dates: a step says "earned" only when the backend
 * does. A yearly plan is not earned during its free trial, and a client who
 * was already paying when invited, a comped client or a client who added the
 * practice from the app earns nothing.
 *
 * Pure functions with type-only imports, so tests/commission.spec.ts runs
 * without a browser.
 */
import type {
  CommissionStatus,
  ConnectionStatus,
  SubscriptionStatus,
} from "@/components/shared/status-badge";

const DAY_MS = 24 * 60 * 60 * 1000;
// The backend gate is 183 days of paid access. Counting months as sixths of
// it keeps "N of 6 months" below 6 for as long as the gate is still closed.
const GATE_DAYS = 183;
const DAYS_PER_GATE_MONTH = GATE_DAYS / 6;

/** When a commission is earned, shown while it is still ahead. */
export const COMMISSION_RULE =
  "Yearly plan: earned when the first year is paid, after any free trial. Monthly plan: earned after 6 months of paid access.";

/** Why a paying client earns the practice nothing. */
export const NOT_A_REFERRAL =
  "Commission applies only to clients who start paying for Taxr Pro after your invite.";

const ADDED_BY_CLIENT =
  "Commission applies only to clients you invite, not to clients who add your practice from the Taxr app.";

export interface CommissionBadgeInput {
  status: CommissionStatus;
  connectionStatus?: ConnectionStatus;
  subscriptionStatus?: SubscriptionStatus;
  clientInitiated?: boolean;
}

/** Label and hover text for the commission badge. */
export function commissionBadgeText(p: CommissionBadgeInput): {
  label: string;
  tooltip: string;
} {
  switch (p.status) {
    case "earning":
      return {
        label: "earning",
        tooltip: `Commission in progress. ${COMMISSION_RULE}`,
      };
    case "earned":
      return { label: "earned", tooltip: "Commission earned for this client." };
    case "expired":
      return {
        label: "expired",
        tooltip:
          "Subscription ended before the commission was earned. This opportunity has expired.",
      };
  }

  const notYet = "No commission yet. Client hasn't subscribed.";
  switch (p.connectionStatus) {
    case "accepted":
      if (p.clientInitiated) {
        return { label: "added by client", tooltip: ADDED_BY_CLIENT };
      }
      // Subscribed, yet the backend pays nothing: comped, or already paying
      // when invited. "Not yet subscribed" would contradict the subscription
      // badge next to it.
      if (p.subscriptionStatus && p.subscriptionStatus !== "free") {
        return { label: "no commission", tooltip: NOT_A_REFERRAL };
      }
      return { label: "not yet subscribed", tooltip: notYet };
    case "declined":
      return { label: "invite declined", tooltip: notYet };
    case "disconnected":
      return { label: "disconnected", tooltip: notYet };
    default:
      return { label: "invite pending", tooltip: notYet };
  }
}

export type CommissionStepState = "complete" | "current" | "pending" | "failed";

export interface CommissionStep {
  label: string;
  description: string;
  state: CommissionStepState;
}

export interface CommissionStepsInput {
  displayName: string;
  inviteEmail: string;
  status: ConnectionStatus;
  subscriptionStatus: SubscriptionStatus;
  commissionStatus: CommissionStatus;
  subscriptionPlanType?: string | null;
  invitedAt?: string | null;
  acceptedAt?: string | null;
  subscriptionStartedAt?: string | null;
  subscriptionEndedAt?: string | null;
  subscriptionExpiresAt?: string | null;
  clientInitiated?: boolean;
}

export function formatDate(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Whole months of paid access from `start` to `end` (or now), counted as
 * sixths of the 183-day gate so the number matches the backend's decision.
 */
export function paidMonths(
  start?: string | null,
  end?: string | null,
  now: Date = new Date(),
): number {
  if (!start) return 0;
  const from = new Date(start).getTime();
  const to = end ? new Date(end).getTime() : now.getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.max(0, Math.floor((to - from) / DAY_MS / DAYS_PER_GATE_MONTH));
}

function noCommission(p: CommissionStepsInput): CommissionStep {
  return {
    label: "No commission",
    description: p.clientInitiated ? ADDED_BY_CLIENT : NOT_A_REFERRAL,
    state: "failed",
  };
}

function commissionStep(
  p: CommissionStepsInput,
  name: string,
  now: Date,
): CommissionStep {
  const yearly = p.subscriptionPlanType === "yearly";
  switch (p.commissionStatus) {
    case "earned":
      return {
        label: "Commission earned",
        description:
          p.subscriptionStatus === "expired"
            ? "Commission was earned before the subscription ended"
            : yearly
              ? "Earned: the first year has been paid"
              : "Earned: 6-month milestone reached",
        state: "complete",
      };
    case "earning":
      return {
        label: "Commission in progress",
        description: yearly
          ? "Earned when the first year is paid, after any free trial"
          : `${Math.min(paidMonths(p.subscriptionStartedAt, null, now), 5)} of 6 months completed`,
        state: "current",
      };
    case "expired":
      return {
        label: "Commission not earned",
        description: yearly
          ? "The subscription ended before the first year was paid"
          : `${name} cancelled after ${Math.min(
              paidMonths(p.subscriptionStartedAt, p.subscriptionEndedAt, now),
              5,
            )} of 6 months required`,
        state: "failed",
      };
    default:
      return noCommission(p);
  }
}

/** The steps of the "Referral progress" sheet, first to last. */
export function deriveCommissionSteps(
  p: CommissionStepsInput,
  now: Date = new Date(),
): CommissionStep[] {
  const steps: CommissionStep[] = [];
  const name = p.displayName || p.inviteEmail;

  steps.push(
    p.clientInitiated
      ? {
          label: "Added by client",
          description: `${name} added your practice from the Taxr app`,
          state: "complete",
        }
      : {
          label: "Invited",
          description: p.invitedAt
            ? `Invite sent ${formatDate(p.invitedAt)}`
            : "Invite sent",
          state: "complete",
        },
  );

  if (p.status === "declined") {
    steps.push({
      label: "Declined",
      description: `${name} declined the invitation`,
      state: "failed",
    });
    return steps;
  }

  if (p.status === "disconnected") {
    steps.push({
      label: "Disconnected",
      description: "Connection was closed",
      state: "failed",
    });
    return steps;
  }

  if (p.status !== "accepted") {
    steps.push({
      label: "Awaiting signup",
      description: `Waiting for ${name} to sign up`,
      state: "current",
    });
    steps.push({
      label: "Subscribed",
      description: "Client starts a trial or subscribes",
      state: "pending",
    });
    steps.push({
      label: "Commission earned",
      description: COMMISSION_RULE,
      state: "pending",
    });
    return steps;
  }

  steps.push({
    label: "Connected",
    description: p.clientInitiated
      ? p.acceptedAt
        ? `Connected on ${formatDate(p.acceptedAt)}`
        : "Connected to your practice"
      : p.acceptedAt
        ? `${name} joined on ${formatDate(p.acceptedAt)}`
        : `${name} accepted the invitation`,
    state: "complete",
  });

  const planLabel =
    p.subscriptionPlanType === "yearly"
      ? ", yearly plan"
      : p.subscriptionPlanType === "monthly"
        ? ", monthly plan"
        : "";

  if (p.subscriptionStatus === "subscribed") {
    steps.push({
      label: `Subscribed${planLabel}`,
      description: p.subscriptionStartedAt
        ? `Started ${formatDate(p.subscriptionStartedAt)}`
        : "Client is subscribed",
      state: "complete",
    });
  } else if (p.subscriptionStatus === "cancelled") {
    steps.push({
      label: `Cancelled${planLabel}`,
      description: p.subscriptionExpiresAt
        ? `Access ends ${formatDate(p.subscriptionExpiresAt)}`
        : "Auto-renew is off, access continues until period end",
      state: "complete",
    });
  } else if (p.subscriptionStatus === "expired") {
    steps.push({
      label: "Subscription ended",
      description: p.subscriptionEndedAt
        ? `Ended ${formatDate(p.subscriptionEndedAt)}`
        : `${name}'s subscription has ended`,
      state: "failed",
    });
  } else {
    steps.push({
      label: "Subscribed",
      description: `Waiting for ${name} to subscribe`,
      state: "current",
    });
    steps.push(
      p.clientInitiated
        ? noCommission(p)
        : {
            label: "Commission earned",
            description: COMMISSION_RULE,
            state: "pending",
          },
    );
    return steps;
  }

  steps.push(commissionStep(p, name, now));
  return steps;
}
